import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Search, Plus, Loader2, ExternalLink, FileText } from "lucide-react";
import { toast } from "sonner";
import { formatRupiah, formatTanggal } from "../lib/format";
import { PageHeader } from "../components/ui/page-header";
import { EmptyState } from "../components/ui/empty-state";

const PAGE_SIZE = 10;

export default function InvoicePage() {
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [fVendor, setFVendor] = useState("all");
    const [page, setPage] = useState(1);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (fVendor !== "all") params.vendor_id = fVendor;
            const { data } = await api.get("/invoice", { params });
            setItems(data);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { (async () => { const v = await api.get("/vendors"); setVendors(v.data); })(); }, []);
    useEffect(() => { const t = setTimeout(() => { load(); setPage(1); }, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, fVendor]);

    const vendorMap = useMemo(() => Object.fromEntries(vendors.map((v) => [v.id, v.nama])), [vendors]);
    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    const pageItems = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div data-testid="invoice-page">
            <PageHeader
                title="Invoice / Tutup Periode"
                description="Tutup periode vendor untuk mengunci transaksi dan menghitung penghasilan final."
                actions={
                    <Button onClick={() => nav("/invoice/new")} data-testid="invoice-add-button" className="rounded-lg gap-2 text-white font-medium" style={{ background: "var(--apple-blue)" }}>
                        <Plus className="w-4 h-4" /> Buat Invoice Baru
                    </Button>
                }
            />

            <div className="rounded-xl bg-white p-4 mb-4" style={{ border: "1px solid var(--border)" }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
                        <Input placeholder="Cari nomor invoice..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="invoice-search-input" className="pl-9 rounded-lg h-10" />
                    </div>
                    <Select value={fVendor} onValueChange={setFVendor}>
                        <SelectTrigger className="rounded-lg h-10"><SelectValue placeholder="Semua vendor" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua vendor</SelectItem>
                            {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.nama}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden space-y-2">
                {loading ? (
                    <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--apple-blue)" }} /></div>
                ) : pageItems.length === 0 ? (
                    <div className="rounded-xl bg-white" style={{ border: "1px solid var(--border)" }}>
                        <EmptyState icon={FileText} title="Belum ada invoice" description="Belum ada invoice/tutup periode." testid="invoice-empty-mobile" />
                    </div>
                ) : pageItems.map((row) => {
                    const tp = Number(row.total_penghasilan_vendor || 0);
                    return (
                        <Link key={row.id} to={`/invoice/${row.id}`} className="block rounded-xl bg-white p-4 hover:bg-slate-50"
                              style={{ border: "1px solid var(--border)" }} data-testid={`invoice-card-${row.id}`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="min-w-0 flex-1">
                                    <div className="font-semibold text-sm" style={{ color: "var(--apple-blue)" }}>{row.nomor_invoice}</div>
                                    <div className="text-xs truncate" style={{ color: "var(--text-primary)" }}>{row.nama_vendor_snapshot}</div>
                                </div>
                                <span className="text-[10px] font-semibold uppercase px-2 py-1 rounded-md border bg-emerald-50 text-emerald-700 border-emerald-200">Tertutup</span>
                            </div>
                            <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{formatTanggal(row.tanggal_mulai)} — {formatTanggal(row.tanggal_akhir)}</div>
                            <div className="flex items-center justify-between pt-2" style={{ borderTop: "1px solid var(--border-soft)" }}>
                                <div className="text-xs" style={{ color: "var(--text-muted)" }}>Tutup {formatTanggal(row.tanggal_tutup)}</div>
                                <div className={`text-sm font-bold num-tabular ${tp >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{formatRupiah(tp)}</div>
                            </div>
                        </Link>
                    );
                })}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block rounded-xl overflow-hidden bg-white" style={{ border: "1px solid var(--border)" }}>
                <Table data-testid="invoice-data-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Nomor</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Vendor</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Periode</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Tutup</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider text-right" style={{ color: "var(--text-muted)" }}>Penghasilan</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Status</TableHead>
                            <TableHead className="text-right text-xs font-semibold uppercase tracking-wider w-24" style={{ color: "var(--text-muted)" }}>Aksi</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={7} className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: "var(--apple-blue)" }} /></TableCell></TableRow>
                        ) : pageItems.length === 0 ? (
                            <TableRow><TableCell colSpan={7}><EmptyState icon={FileText} title="Belum ada invoice" description="Belum ada invoice/tutup periode." testid="invoice-empty-desktop" /></TableCell></TableRow>
                        ) : pageItems.map((row) => {
                            const tp = Number(row.total_penghasilan_vendor || 0);
                            return (
                                <TableRow key={row.id} data-testid={`invoice-row-${row.id}`}>
                                    <TableCell><Link to={`/invoice/${row.id}`} className="font-semibold hover:underline" style={{ color: "var(--apple-blue)" }}>{row.nomor_invoice}</Link></TableCell>
                                    <TableCell className="text-sm" style={{ color: "var(--text-primary)" }}>{row.nama_vendor_snapshot}</TableCell>
                                    <TableCell className="text-sm" style={{ color: "var(--text-muted)" }}>{formatTanggal(row.tanggal_mulai)} — {formatTanggal(row.tanggal_akhir)}</TableCell>
                                    <TableCell className="text-sm" style={{ color: "var(--text-muted)" }}>{formatTanggal(row.tanggal_tutup)}</TableCell>
                                    <TableCell className={`text-sm font-bold text-right num-tabular ${tp >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{formatRupiah(tp)}</TableCell>
                                    <TableCell><span className="text-[10px] font-semibold uppercase px-2 py-1 rounded-md border bg-emerald-50 text-emerald-700 border-emerald-200">Tertutup</span></TableCell>
                                    <TableCell className="text-right">
                                        <Link to={`/invoice/${row.id}`} data-testid={`invoice-detail-link-${row.id}`} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-[var(--apple-blue-soft)]" style={{ color: "var(--apple-blue)" }}><ExternalLink className="w-3.5 h-3.5" /> Detail</Link>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                {!loading && items.length > 0 && (
                    <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: "1px solid var(--border)", background: "var(--bg-subtle)" }}>
                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>{(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, items.length)} dari {items.length}</div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-lg h-8">Sebelumnya</Button>
                            <span className="text-xs px-2" style={{ color: "var(--text-secondary)" }}>Hal {page}/{totalPages}</span>
                            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="rounded-lg h-8">Selanjutnya</Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
